import React, { useEffect, useState } from 'react';
import ProjectListItemPlaceholder from '../../components/UI/Placeholder/ProjectListItemPlaceholder/ProjectListItemPlacholder';
import CallToActionPlaceholder from '../../components/UI/Placeholder/CallToActionPlaceholder/CallToActionPlaceholder';
import ProjectListItem from './ProjectListItem/ProjectListItem';
import ProjectListCounter from './ProjectListCounter/ProjectListCounter';
import CallToAction from '../../components/CallToAction/CallToAction';
import styles from './ProjectList.module.scss';
import axios from '../../axios-base';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { Row, Col } from 'reactstrap';
import ECB from '../../components/ECB/ECB';
import { IconSearch } from '@tabler/icons-react';
import FormInput from '../../components/UI/Input/FormInput';
import { sendVirtualPageView } from '../../shared/utility';
import qs from 'qs';
import { Helmet } from 'react-helmet';
import { usePhases, useJoinedCategories } from '../../store/configStore';

function ProjectListHook(props) {
    const votingPhases = usePhases();
    const joinedCategories = useJoinedCategories();

    const [projectsCount, setProjectsCount] = useState(0);
    const [projects, setProjects] = useState(null);
    const [phrase, setPhrase] = useState('');
    const [categoryOptions, setCategoryOptions] = useState(null);
    const [categorySelected, setCategorySelected] = useState('');
    const [cta, setCta] = useState(null);
    const [ecb, setEcb] = useState([]);
    const [inputSelection, setInputSelection] = useState(true);

    let history = useHistory();
    const location = useLocation();

    useEffect(() => {
        const cta = axios.get('/cta/', { params: { page: 'projekte' } });
        const options = axios.get('/options/');
        const ecbRequest = axios.get('/ecb/');

        Promise.all([cta, options, ecbRequest])
            .then(([resCta, resOptions, resEcb]) => {
                setCta(resCta.data[0]);
                setEcb(resEcb.data);

                let availableCategories = resOptions.data[0].projectForm.projectCategory;
                if (availableCategories) {
                    availableCategories.dependent = joinedCategories;
                }
                if (availableCategories && availableCategories.elementConfig.options.length > 1) {
                    let firstOption = () => {
                        if (availableCategories.dependent) {
                            return { value: '', displayValue: 'Alle' };
                        } else {
                            return {
                                value: '',
                                displayValue: 'Eine ' + availableCategories.elementConfig.label + ' wählen',
                                disabled: true,
                            };
                        }
                    };
                    availableCategories.elementConfig.options.unshift(firstOption());
                    availableCategories.elementConfig.label =
                        'Teilnehmende nach ' + availableCategories.elementConfig.label + ' filtern';
                    setCategoryOptions(availableCategories);
                }
            })
            .catch((error) => {
                console.log(error);
            });
        sendVirtualPageView('Teilnehmer');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const query = qs.parse(location.search.slice(1)).category;
        setProjects(null);
        axios
            .get(`/projects/?category=${query}`)
            .then((response) => {
                setProjects(response.data);
                setProjectsCount(response.data.length);
                setCategorySelected(query !== undefined ? query : '');
                setInputSelection(true);
            })
            .catch((error) => {
                console.log(error);
            });
    }, [location]);

    const categoryHandler = (e) => {
        const selectedCategory = e.target.value;
        setPhrase('');
        setInputSelection(false);
        if (selectedCategory !== categorySelected) {
            setCategorySelected(selectedCategory);
            history.push({
                search: `?category=${selectedCategory}`,
            });
        }
    };

    const onChangeHandler = (e) => {
        setPhrase(e.target.value);
        if (categorySelected !== '' && categoryOptions.dependent) {
            setCategorySelected('');
            history.push({
                search: `?category=`,
            });
        }
    };

    let categoryForm;
    if (categoryOptions) {
        categoryForm = (
            <FormInput
                wrapperCSSClass="category-select mb-3"
                elementType={categoryOptions.elementType}
                elementConfig={categoryOptions.elementConfig}
                value={categorySelected}
                changed={(e) => categoryHandler(e)}
                disabled={!inputSelection}
                analyticsClassName="anl_project-list--category-select"
                shouldValidate={categoryOptions.valid}
                helperText={`${categoryOptions.elementConfig.label}, wie z.B. ${categoryOptions.elementConfig.options[1].displayValue} oder ${categoryOptions.elementConfig.options[2].displayValue}`}
            />
        );
    }

    let projectsRender = (
        <>
            <ProjectListItemPlaceholder />
            <ProjectListItemPlaceholder />
            <ProjectListItemPlaceholder />
        </>
    );

    if (projects) {
        const { appStatus, voteStarted } = votingPhases;

        projectsRender = projects
            .filter((project) => {
                // Filter by finalist only in FINALE phase
                if (appStatus === 'FINALE') {
                    return project.isFinalist || !votingPhases.hasQualification;
                }
                return true;
            })
            .filter((project) =>
                (
                    project.contestantName.toLowerCase() +
                    (project.projectTitle ? ' ' + project.projectTitle.toLowerCase() : '')
                ).includes(phrase.toLowerCase())
            )
            .reduce((displayedProjects, project, index) => {
                let projectVoteCount = '-';
                let projectRank = '-';

                if (appStatus === 'FINALE') {
                    if (project.voteCountFinale) {
                        projectVoteCount = project.voteCountFinale;
                        projectRank = project.rank;
                    }
                } else if (appStatus === 'QUALIFICATION' && voteStarted) {
                    if (project.voteCount) {
                        projectVoteCount = project.voteCount;
                        projectRank = project.rank;
                    }
                } else if (appStatus === 'CLOSED') {
                    // Show final counts
                    if (votingPhases.hasFinale) {
                        projectVoteCount = project.voteCountFinale || '-';
                    } else {
                        projectVoteCount = project.voteCount || '-';
                    }
                    projectRank = project.rank || '-';
                }

                let projectLink = // Todo: Deep-Link zur Ressource definieren und Pretty-Link erstellen
                    (
                        <Link
                            className={`${styles.ProjectListLink} text-decoration-none anl_project-list--project-lnk`}
                            to={'/projekte/' + project._id}
                            key={project._id}
                        >
                            <ProjectListItem
                                rank={projectRank}
                                title={project.contestantName}
                                subtitle={project.projectTitle}
                                voteCount={projectVoteCount}
                                aria-hidden={true}
                            />
                            <p className="visually-hidden">
                                {`Platz ${projectRank}: ${project.contestantName}${project.projectTitle ? ' (' + project.projectTitle + ')' : ''} mit ${projectVoteCount} Stimmen`}
                            </p>
                        </Link>
                    );

                // Inject ECB where its "order" matches the current 0-based index
                const ecbCard = ecb.find(
                    (item) => item?.isVisible && typeof item.order === 'number' && item.order === index
                );
                if (ecbCard) {
                    displayedProjects.push(
                        <div key={`ecb-${ecbCard._id}`} className={`${styles.ProjectListLink} border-bottom my-0`}>
                            <ECB
                                assets={ecbCard.assets}
                                altText={ecbCard.altText}
                                link={ecbCard.link}
                                index={ecbCard.order}
                            />
                        </div>
                    );
                }

                displayedProjects.push(projectLink);
                return displayedProjects;
            }, []);
    }

    let ctaRender = <CallToActionPlaceholder />;

    if (cta) {
        ctaRender = (
            <CallToAction
                btnClass={`anl_cta--${cta.page}--btn-${cta.position}`}
                headline={cta.headline}
                copy={cta.copy}
                buttonText={cta.buttonText}
                buttonColor={cta.buttonColor}
                linkUrl={cta.linkUrl}
                outline={cta.outline}
                buttonVisibility={cta.buttonVisibility}
            />
        );
    }

    return (
        <>
            <Helmet>
                <title>{`Teilnehmende | ${process.env.REACT_APP_PROJECT_TITLE}`}</title>
            </Helmet>

            {/* CallToAction */}
            <Row className="my-3">
                <Col>{ctaRender}</Col>
            </Row>
            <Row className={styles.ProjectListCounterContainer + ' bg-light pt-3 p-0 border-top border-bottom'}>
                {categoryForm}
                <Col className="project-search">
                    <FormInput
                        disabled={!inputSelection}
                        changed={onChangeHandler}
                        inlineIcon={IconSearch}
                        analyticsClassName="anl_project-list--search-bar"
                        label="Teilnehmende suchen"
                        elementConfig={{
                            label: 'Teilnehmende suchen',
                            placeholder: 'Musterverein e.V.',
                            type: 'search',
                        }}
                        elementType="search"
                        helperText="Freitextsuche nach einem Teilnehmenden"
                        value={phrase}
                    />
                </Col>
            </Row>
            <Row className="my-3">
                <ProjectListCounter projectsCount={projectsCount} categories={categoryOptions} />
            </Row>
            <Row>
                <Col className={styles.ProjectResults + ' px-0 min-vh-100'}>{projectsRender}</Col>
            </Row>
        </>
    );
}

export default ProjectListHook;
